import _ from "lodash";
import { CustomFormUploadProps, parseCard, updateRequestValue } from "../../shared/components/TicketTemplateAnswer";
import { platformService } from "../../shared/services/platform.service";
import { parseIfJSON, toPascalCase } from "../../shared/utils/helper";
import { SEND_FORM_OPTION } from "./constants";
import { CustomForm } from "./CustomForm.Interface";
import { AdaptiveCardTexts } from "../../shared/utils/constants";
import { ICustomProperty } from "../../shared/interfaces/customproperties.interface";
import { Comment } from '../../shared/interfaces/ticket.interface';

export const buildCardAnswerJSON = (customForms : any) => {
  if(customForms == undefined || customForms.length<=0)
    return "{}";

  let answerList = [];
  customForms.forEach(form => {
    const formName = toPascalCase(form.Name);
    answerList.push({[formName]: form.CardAnswerJson});
  });
  const mergedAnswers = Object.assign({}, ...answerList);
  return JSON.stringify(mergedAnswers);
}

export const findCardInputItems = (obj: any, customProperties: ICustomProperty[]) => {
  let results = [];
  if (Array.isArray(obj)) {
    for (const el of obj) {
       results = results.concat(findCardInputItems(el, customProperties));
    }
  } else if (obj && typeof obj === "object") {
    if (typeof obj.type === "string" && (obj.type.startsWith("Input."))) {
      results.push(obj);
    }

    if(typeof obj?.id === "string" && obj.id.startsWith("Ticket.")){
      results.push(obj);
    }

    if(typeof obj?.id === "string" && obj?.isCustomProperty && obj?.dataType){
      const cp = customProperties.some(c => c.PropertyId == obj.id && !c.IsHidden && !c.IsArchived);
      if(cp)
        results.push(obj);
    }

    // Also explore nested properties
    for (const key of Object.keys(obj)) {
      results = results.concat(findCardInputItems(obj[key], customProperties));
    }
  }
  return results;
}

export const updateCustomFormState = (customFormList: CustomForm[], setContextState: any) => {
  customFormList = customFormList.map(x=>({
            ...x,
            CardTemplateJson: parseIfJSON(x.CardTemplateJson),
            CardAnswerJson:  parseIfJSON(x.CardAnswerJson)
          })
        );
  setContextState(prevState => ({
      ...prevState,
      customFormState: {
          ...prevState.customFormState,
          customForms: customFormList
        },
    }));
}

export const updateActiveFormState = (newActiveForm: CustomForm, setContextState: any) => {
  setContextState(prevState => ({
      ...prevState,
      customFormState: {
          ...prevState.customFormState,
          activeForm: newActiveForm
        },
    }));
}

export const updateIsSummaryPageState = (isSummaryPage: boolean, setContextState: any) => {
  setContextState(prevState => ({
      ...prevState,
      customFormState: {
          ...prevState.customFormState,
          isSummaryPage: isSummaryPage
        },
    }));
}

export const updateIsCustomFormSavingState = (isSaving: boolean, setContextState: any) => {
  setContextState(prevState => ({
      ...prevState,
      customFormState: {
          ...prevState.customFormState,
          isCustomFormSaving: isSaving
        },
    }));
}


export const getDefaultCustomFormList = (templateForms: any, currentState: any, dataSourceList: any) => {
  const sortedTemplateForms = [...templateForms].sort((a, b) => a.Order - b.Order);
  const customFormList =  sortedTemplateForms.map((templateForm)=>{
    const templateJSON = JSON.parse(templateForm.Form?.Definition ?? '{"body":[]}');
    const answerJSON = JSON.parse("{}");
    const isAnswered = templateForm.Order == 0;
     
    parseCard(templateJSON.body, answerJSON, currentState, {}, dataSourceList);

    return {
      TemplateFormId: templateForm.TemplateFormId ?? templateForm.Id,
      Name: templateForm.Name,
      Order: templateForm.Order,
      SubmitButtonText: templateForm.SubmitButtonText,
      CardTemplateJson: templateJSON,
      CardAnswerJson: answerJSON,
      IsAnswered: isAnswered
    };
  });
  return customFormList;
}

export const setDefaultCustomFormState = (templateForms: any, currentState: any, dataSourceList: any, setContextState: any) =>{
    const customFormList = getDefaultCustomFormList(templateForms, currentState, dataSourceList);
    const initialForm = customFormList[0];
    if(initialForm != null)
      initialForm.IsAnswered = true;

    updateCustomFormState(customFormList, setContextState);
    updateActiveFormState(initialForm, setContextState);
}

export const handleNextCustomFormAction = async(templateId: any, currentState: any, setContextState: any, setCustomFormButtonState: any, ticketId: any) => {
    if(currentState.openSendFormDialogFrom == "applyTemplate")
      ticketId = 0;

    const api = new platformService();
    const customForms = currentState.customFormState.customForms;
    const currentForm = currentState.customFormState.activeForm;
    const executeTransitionResult = await api.executeFormTransition(templateId, currentForm.TemplateFormId, currentForm, ticketId);
    const resultData = executeTransitionResult.data ?? null;
    const nextForm = (resultData !=null) ? customForms[resultData.Order] : null; 
    const isSummaryPage = (nextForm == null || nextForm == undefined);
    
    const customFormWithAnswersList = customForms.map(customForm => {
      const answeredForm = currentForm ?? nextForm;
      if (customForm.TemplateFormId == answeredForm.TemplateFormId) {
        return { ...customForm, IsAnswered: true, CardAnswerJson: answeredForm.CardAnswerJson };
      } else {
        return { ...customForm };
      }
    });
    updateCustomFormState(customFormWithAnswersList, setContextState);
    
    updateActiveFormState(nextForm, setContextState);
    updateIsSummaryPageState(isSummaryPage, setContextState);

    setCustomFormButtonState(preState => ({
      ...preState,
      showBackButton: (nextForm && nextForm.Order > 0) || isSummaryPage,
      showNextButton:  !isSummaryPage,
      showStartOverButton: isSummaryPage,
      showSubmitButton:  isSummaryPage
    }));

    
  }

  export const handleBackCustomFormAction = (currentState: any, setContextState: any, setCustomFormButtonState: any) => {
    const customForms = currentState.customFormState.customForms as CustomForm[];
    const currentForm = currentState.customFormState.activeForm as CustomForm;
    const allAnsweredForms = customForms.filter(x=>x.IsAnswered);
    const lastAnsweredIndex = _.findLastIndex(allAnsweredForms, x=>x.TemplateFormId==currentForm?.TemplateFormId);
    const prevFormIndex = lastAnsweredIndex >=1 ? lastAnsweredIndex-1 : allAnsweredForms.length-1;
    const nextForm = allAnsweredForms[prevFormIndex];
    const isSummaryPage = (nextForm == null || nextForm == undefined);
    
    if(lastAnsweredIndex > 0){
      const formsToSetUnanswered = allAnsweredForms.splice(lastAnsweredIndex);
      const resetAnsweredForms = customForms.map(customForm => {
        const match = formsToSetUnanswered.filter(x=>x.TemplateFormId == customForm.TemplateFormId)[0];
        if (match) {
          return { ...customForm, IsAnswered: false, CardAnswerJson: match.CardAnswerJson };
        } else {
          return { ...customForm };
        }
      });
      updateCustomFormState(resetAnsweredForms, setContextState);
    }

    updateActiveFormState(nextForm, setContextState);
    updateIsSummaryPageState(false, setContextState);

    setCustomFormButtonState(preState => ({
      ...preState,
      showBackButton: (nextForm && nextForm.Order > 0) || isSummaryPage,
      showNextButton:  !isSummaryPage,
      showStartOverButton: isSummaryPage,
      showSubmitButton:  isSummaryPage
    }));

   
  }

  export const handleStartOVerCustomFormAction = (templateForms: any, currentState: any, dataSourceList: any, setContextState: any, setCustomFormButtonState: any) => {
    setDefaultCustomFormState(templateForms, currentState, dataSourceList,  setContextState);
    updateIsSummaryPageState(false, setContextState);
    
   
    setCustomFormButtonState(preState => ({
      ...preState,
      showBackButton: false,
      showNextButton:  true,
      showStartOverButton: false,
      showSubmitButton:  false
    }));
  }

  export const handleSendCustomForm = async(formState: any, currentState: any, setContextState: any) => {
    try {
      let sendFormToId = "";
      let sendFormViaTVA = true;
            
      const api = new platformService();

      switch(formState.SelectedTemplate.SendCustomFormSettings.Type){
        case SEND_FORM_OPTION.SEND_TO_REQUESTER:
          sendFormToId = formState.RequesterId;
          break;
        case SEND_FORM_OPTION.SEND_TO_ASSIGNEE:
          sendFormToId = formState.AssigneeId;
          sendFormViaTVA = false;
          break;
        case SEND_FORM_OPTION.SEND_TO_AFFECTEDUSER:
          sendFormToId = formState.SelectedTemplate.SendCustomFormSettings.SelectedAffectedUserId;
          break;
        case SEND_FORM_OPTION.SEND_TO_COLLABORATOR:
          sendFormToId = formState.SelectedTemplate.SendCustomFormSettings.SelectedCollaboratorId;
          sendFormViaTVA = false;
          break;
      }

      setContextState((prev)=>({
        ...prev,
        isSendFormLoading: true
      }));
    
      await api.sendCustomFormToConversation({ 
        TicketId: formState.Id, 
        TemplateId: formState.SelectedTemplate.Id ?? formState.TemplateId, 
        UserId: sendFormToId, 
        IsTVA: sendFormViaTVA, 
        IsShowTyping: true,
        IsSendWithCustomAnswer: currentState.customFormState.isSendCurrentAnswers,
        TenantId: currentState?.teamsConfig?.TenantId}); 
    } catch (e) {
          console.error(e);
    }
  }

  export const validateRequiredInputs = (cardPayload: any, cardAnswer: any, customProperties: ICustomProperty[]) => {
    // get required field ids (exclude hidden fields)
    const requiredIds = cardPayload.body
      .filter((item: any) => {
        let includeCustomProperty = true;
        if(item.isCustomProperty && item.dataType)
          includeCustomProperty = customProperties.some(c => c.PropertyId == item.id && !c.IsHidden && !c.IsArchived);
          
        return (item.requireField || item.isRequired) && item.isVisible !== false && item.visibleField !== false && includeCustomProperty;
      })
      .map((item: any) => item.id);

    // include Ticket.Request if it exists in cardAnswer and is visible
    if (cardAnswer && Object.hasOwn(cardAnswer, "Ticket.Request")) {
      if (!requiredIds.includes("Ticket.Request")) {
        const ticketRequestField = cardPayload.body.find(
          (item: any) => item.id === "Ticket.Request"
        );

        if (ticketRequestField && ticketRequestField.isVisible !== false && ticketRequestField.visibleField !== false) 
        {
          requiredIds.push("Ticket.Request");
        }
      }
    }

    // find missing required answers
    const missing = requiredIds.filter((id: string) => {
      const value = cardAnswer?.[id];
      if(id === "Ticket.AddComment"){
        let commentObj = JSON.parse(value) as Comment;
        return value === undefined || value === null || value === "" || (commentObj.Body.trim().length === 0);
      }
      return value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    });

    // remove previous errors
    document.querySelectorAll(".adaptive-error").forEach(el => el.remove());

    if (missing.length > 0) {
      missing.forEach((id: string) => {
        const def = cardPayload.body.find((i: any) => i.id === id);

        const elements = document.querySelectorAll(`[id="${id}"]`);
        const element = elements[elements.length - 1];

        if (element) {
          const err = document.createElement("div");
          err.className = "adaptive-error";
          err.textContent = def?.errorMessage || AdaptiveCardTexts.REQUIRE_FIELD;
          err.style.color = "red";
          err.style.fontSize = "0.9em";
          err.style.marginTop = "4px";
          err.style.marginBottom = "8px";

          element.after(err);
        }
      });

      return false;
    }

    return true;
  };
  //#region request portal
  export const updateCustomFormListWithAnswers = (customForms: CustomForm[], activeForm: CustomForm, setCustomForms: any) => {
    const customFormWithAnswersList = customForms.map(customForm => {
      if (customForm.TemplateFormId == activeForm.TemplateFormId) {
        return { ...customForm, CardAnswerJson: activeForm.CardAnswerJson, IsAnswered: true };
      } else {
        return { ...customForm };
      }
    });
    setCustomForms(preState => (customFormWithAnswersList));
  }

  export const setCustomFormTemplateData = (currentTemplate: any, setCurrentTemplate: any, setCustomForms: any, setActiveForm: any, currentState: any, dataSourceList: any)=>{
    setCurrentTemplate(currentTemplate);
    const customFormList =  getDefaultCustomFormList(currentTemplate.TemplateForms, currentState, dataSourceList);
    setCustomForms(customFormList);
    setActiveForm(customFormList[0]);
  }
  
  export const parseCardTemplate = (cardTemplateJson: any, cardAnswerJson, currentState, dataSourceList, extraProps?: CustomFormUploadProps, requestMessage?: string)=>{
    const cardPayload = parseIfJSON(cardTemplateJson);
        
    if(cardPayload){
      parseCard(cardPayload.body, cardAnswerJson, currentState, extraProps, dataSourceList);
      if(requestMessage){
        updateRequestValue(cardPayload.body, cardAnswerJson, currentState, requestMessage);
      }
    }
      
      
    return JSON.stringify(cardPayload);
  }

  export const markFormsAsUnanswerd = (allAnsweredForms: CustomForm[], customForms: CustomForm[], lastAnsweredIndex: any, setCustomForms: any) =>{
    const formsToSetUnanswered = allAnsweredForms.splice(lastAnsweredIndex);
    const resetAnsweredForms = customForms.map(customForm => {
      const match = formsToSetUnanswered.filter(x=>x.TemplateFormId == customForm.TemplateFormId)[0];
      
      if (match) {
        return { ...customForm, IsAnswered: false };
      } else {
        return { ...customForm };
      }
    });
    
    setCustomForms(preState => (resetAnsweredForms));
  }
  //#endregion request portal